import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

const tipImobilLabels = {
  apartament: "apartamentul",
  casa: "casa",
  spatiu_comercial: "spatiul comercial",
};

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());
  const tipImobilText = data.imobilTip === "altul"
    ? (data.imobilTipAltul || "imobilul")
    : (tipImobilLabels[data.imobilTip] || "imobilul");
  const ascundeCamere = data.imobilTip === "spatiu_comercial" || data.imobilTip === "altul";
  const dataInceputFmt = data.dataInceput
    ? fmtDate(new Date(data.dataInceput))
    : "___";
  const durateLuni = parseInt(data.durata) || 12;
  let dataEndFmt = "___";
  if (data.dataInceput) {
    const end = new Date(data.dataInceput);
    end.setMonth(end.getMonth() + durateLuni);
    dataEndFmt = fmtDate(end);
  }

  const ciComodantFmt = !data.comodantPJ && data.comodantCI ? formatCI(data.comodantCI) : "___ ___";
  const ciComodatarFmt = !data.comodatarPJ && data.comodatarCI ? formatCI(data.comodatarCI) : "___ ___";

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = { ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" };
  const bullet = { ...p, paddingLeft: isMobile ? "20px" : "36px", margin: "2px 0" };

  const interdictii = [];
  if (!data.fumatPermis) interdictii.push("fumatul in interior");
  if (!data.animalePermise) interdictii.push("detinerea de animale (fara acord scris)");
  interdictii.push("schimbarea incuietorilor fara acord");

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Comodat
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "4px 0 0 0",
        }}>
          (folosinta gratuita)
        </p>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "4px 0 0 0",
        }}>
          Nr. ___ din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>

        <p style={p}><span style={bold}>1.1. Comodantul:</span></p>
        {data.comodantPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.comodantFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.comodantCUI || "________________________"}</span></p>
            {data.comodantRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.comodantRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.comodantSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.comodantReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.comodantNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.comodantCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciComodantFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.comodantAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Comodatarul:</span></p>
        {data.comodatarPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.comodatarFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.comodatarCUI || "________________________"}</span></p>
            {data.comodatarRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.comodatarRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.comodatarSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.comodatarReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.comodatarNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.comodatarCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciComodatarFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.comodatarAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de comodat, in conformitate cu dispozitiile art. 2146-2157 din Codul Civil, in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Comodantul transmite Comodatarului, cu titlu gratuit, dreptul de folosinta asupra imobilului de tip <span style={bold}>{tipImobilText}</span> situat in {data.imobilAdresa || "____________________________"}{!ascundeCamere && data.imobilCamere ? `, compus din ${data.imobilCamere} camere` : ""}, cu suprafata utila de {data.imobilSuprafata || "___"} mp{data.imobilEtaj ? `, etajul ${data.imobilEtaj}` : ""}.
        </p>
        <p style={p}>
          <span style={bold}>2.2.</span> Imobilul se preda {data.mobilat ? "mobilat si utilat" : "nemobilat"}, in stare corespunzatoare de folosinta. Predarea imobilului se face pe baza de proces-verbal de predare-primire, care constituie anexa la prezentul contract.
        </p>
        <p style={p}>
          <span style={bold}>2.3.</span> Prezentul contract este cu titlu gratuit, Comodatarul neavand obligatia de a plati chirie sau orice alta contraprestatie pentru folosinta imobilului.
        </p>
      </div>

      {/* III. DESTINATIA IMOBILULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. DESTINATIA IMOBILULUI</h3>
        <p style={p}>
          <span style={bold}>3.1.</span> Imobilul va fi folosit exclusiv ca locuinta. Este interzisa desfasurarea de activitati comerciale sau inregistrarea de sedii sociale fara acordul scris al Comodantului.
        </p>
        <p style={p}>
          <span style={bold}>3.2.</span> Numarul maxim de persoane care pot locui in imobil este de <span style={bold}>{data.nrMaxPersoane || "___"}</span> persoane.
        </p>
      </div>

      {/* IV. DURATA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. DURATA CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> Contractul se incheie pe durata de <span style={bold}>{durateLuni} luni</span>, incepand cu data de <span style={bold}>{dataInceputFmt}</span> si pana la data de <span style={bold}>{dataEndFmt}</span>.
        </p>
        <p style={p}>
          <span style={bold}>4.2.</span> Prelungirea se poate face doar prin acord scris (act aditional), cu minimum {data.notificareZile || "30"} zile inainte de expirare.
        </p>
      </div>

      {/* V. OBLIGATIILE COMODANTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>V. OBLIGATIILE COMODANTULUI</h3>
        {[
          "Sa predea imobilul in stare corespunzatoare de folosinta.",
          "Sa asigure Comodatarului folosinta linistita si utila a imobilului pe toata durata contractului.",
          "Sa efectueze reparatiile majore si structurale ale imobilului.",
          "Sa nu tulbure folosinta Comodatarului, cu exceptia verificarilor efectuate cu preaviz de minimum 24 ore.",
          "Sa informeze Comodatarul despre orice vicii ascunse ale imobilului pe care le cunoaste.",
        ].map((text, i) => (
          <p key={i} style={indent}>
            <span style={bold}>5.{i + 1}.</span> {text}
          </p>
        ))}
      </div>

      {/* VI. OBLIGATIILE COMODATARULUI */}
      <h3 style={sectionTitle()}>VI. OBLIGATIILE COMODATARULUI</h3>
      {[
        "Sa conserve imobilul ca un bun proprietar si sa il foloseasca conform destinatiei stabilite.",
        "Sa foloseasca imobilul cu prudenta si diligenta, ca si cum ar fi propriul sau bun.",
        "Sa nu aduca modificari imobilului fara acordul scris al Comodantului.",
        "Sa permita accesul Comodantului pentru verificari, cu preaviz de 24 ore.",
        "Sa anunte imediat Comodantul despre orice defectiune sau deteriorare aparuta.",
        "Sa efectueze reparatiile minore si de intretinere curenta, pe cheltuiala proprie.",
        "Sa restituie imobilul la incetarea contractului, liber de persoane si bunuri, in starea in care l-a primit, mai putin uzura normala.",
      ].map((text, i) => (
        <p key={i} style={indent}>
          <span style={bold}>6.{i + 1}.</span> {text}
        </p>
      ))}
      <p style={indent}>
        <span style={bold}>6.8.</span> {!data.subcomodarePermisa
          ? "Comodatarul nu are drept de subcomodat, subinchiriere sau cedare a folosintei imobilului sub orice forma, fara acordul scris al Comodantului, sub sanctiunea rezilierii de drept a contractului."
          : "Comodatarul poate permite tertilor sa foloseasca imobilul fara acordul prealabil al Comodantului."}
      </p>
      <p style={indent}><span style={bold}>6.9.</span> Este interzis:</p>
      {interdictii.map((text, i) => (
        <p key={i} style={bullet}>- {text}</p>
      ))}

      {/* VII. CHELTUIELI SI UTILITATI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VII. CHELTUIELI SI UTILITATI</h3>
        {data.utilitatiComodatar ? (
          <>
            <p style={p}>
              <span style={bold}>7.1.</span> Comodatarul suporta integral cheltuielile de folosinta curenta ale imobilului: energie electrica, apa, gaz, internet, salubritate, intretinere si orice alte utilitati.
            </p>
            <p style={p}>
              <span style={bold}>7.2.</span> Facturile vor fi achitate de Comodatar la termenele prevazute de furnizorii de utilitati.
            </p>
            <p style={p}>
              <span style={bold}>7.3.</span> Impozitul pe proprietate si asigurarea imobilului raman in sarcina Comodantului, cu exceptia utilitatilor curente suportate de Comodatar conform art. 7.1.
            </p>
          </>
        ) : (
          <>
            <p style={p}>
              <span style={bold}>7.1.</span> Cheltuielile de folosinta curenta (utilitati, intretinere) sunt suportate de Comodant.
            </p>
            <p style={p}>
              <span style={bold}>7.2.</span> Impozitul pe proprietate si asigurarea imobilului raman in sarcina Comodantului.
            </p>
          </>
        )}
      </div>

      {/* VIII. INCETAREA CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VIII. INCETAREA CONTRACTULUI</h3>
        <p style={p}><span style={bold}>8.1.</span> Contractul inceteaza:</p>
        {[
          "la expirarea duratei",
          "prin acordul scris al partilor",
          "prin reziliere de plin drept, fara interventia instantei, in cazul neexecutarii obligatiilor esentiale de catre una dintre parti",
          `prin denuntare unilaterala cu preaviz de ${data.notificareZile || "30"} zile`,
        ].map((text, i) => (
          <p key={i} style={{ ...indent, paddingLeft: isMobile ? "20px" : "40px" }}>
            <span style={{ color: "#c45d3e", fontWeight: 600 }}>{"abcd"[i]})</span> {text}
          </p>
        ))}
        <p style={{ ...p, marginTop: "12px" }}>
          <span style={bold}>8.2.</span> Comodantul poate cere restituirea imobilului inainte de termen daca Comodatarul nu respecta destinatia sau obligatiile contractuale.
        </p>
        <p style={p}>
          <span style={bold}>8.3.</span> In caz de nevoie urgenta si neprevazuta, Comodantul poate solicita restituirea imobilului inainte de termen, cu un preaviz rezonabil.
        </p>
      </div>

      {/* IX. RASPUNDERE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IX. RASPUNDERE</h3>
        <p style={indent}><span style={bold}>9.1.</span> Comodatarul raspunde pentru orice deteriorare a imobilului cauzata din culpa sa sau a persoanelor carora le-a permis accesul.</p>
        <p style={indent}><span style={bold}>9.2.</span> Uzura normala rezultata din folosinta conform destinatiei nu constituie dauna.</p>
        <p style={indent}><span style={bold}>9.3.</span> Comodatarul raspunde pentru pieirea imobilului, daca aceasta se datoreaza folosirii necorespunzatoare sau daca ar fi putut salva imobilul inlocuindu-l cu propriul sau bun.</p>
      </div>

      {/* X. FORTA MAJORA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>X. FORTA MAJORA</h3>
        <p style={indent}><span style={bold}>10.1.</span> Forta majora exonereaza de raspundere partea care o invoca, in conditiile legii, daca este notificata celeilalte parti in termen rezonabil de la aparitia evenimentului.</p>
        <p style={indent}><span style={bold}>10.2.</span> Prin forta majora se intelege orice eveniment extern, imprevizibil, absolut invincibil si inevitabil, conform art. 1351 alin. (2) din Codul Civil.</p>
      </div>

      {/* XI. LITIGII */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>XI. LITIGII</h3>
        <p style={indent}><span style={bold}>11.1.</span> Litigiile decurgand din executarea prezentului contract se solutioneaza pe cale amiabila, iar in caz contrar de catre instantele judecatoresti competente.</p>
      </div>

      {/* XII. DISPOZITII FINALE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>XII. DISPOZITII FINALE</h3>
        <p style={indent}><span style={bold}>12.1.</span> Prezentul contract poate constitui titlu executoriu numai in conditiile prevazute de lege.</p>
        <p style={indent}><span style={bold}>12.2.</span> Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti.</p>
        <p style={indent}><span style={bold}>12.3.</span> Partile declara ca datele de identificare furnizate sunt reale si corespund actelor de identitate.</p>
        <p style={indent}><span style={bold}>12.4.</span> Contractul se incheie in 2 (doua) exemplare originale, cate unul pentru fiecare parte.</p>
      </div>

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            COMODANT
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.comodantPJ
              ? (data.comodantFirma ? `${data.comodantFirma} prin ${data.comodantReprezentant || ""}` : "")
              : (data.comodantNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            COMODATAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.comodatarPJ
              ? (data.comodatarFirma ? `${data.comodatarFirma} prin ${data.comodatarReprezentant || ""}` : "")
              : (data.comodatarNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ContractPreview;
